import api from './api.service';
import type { RejectionPayload } from './rejection.service';
import type { PoMilestoneDTO, PoMilestoneInput } from './poMilestone.service';

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface PurchaseOrderItemDTO {
  id: string;
  po_id: string;
  line_number: number;
  item_code?: string;
  item_description: string;
  supplier_description?: string | null;
  ordered_quantity: number;
  received_quantity?: number;
  invoiced_quantity?: number;
  uom?: string;
  length?: string | null;
  stock_quantity?: number | null;
  stock_uom?: string | null;
  stock_dimensions?: string | null;
  unit_price: number;
  line_total: number;
  discount_percentage?: number;
  discount_amount?: number;
  net_amount: number;
  /** Allocation base (net after line discount). */
  base_amount?: number;
  allocated_overhead_amount?: number;
  loaded_amount?: number;
  landed_unit_cost?: number;
  vat_applicable?: boolean;
  vat_percentage?: number;
  vat_amount?: number;
  expected_delivery_date?: string;
  project_id?: string;
  project?: { id: string; project_name?: string };
  cost_center_id?: string;
  wbs_node_id?: string | null;
  cbs_code_id?: string | null;
  cost_category?: string | null;
  /** Item-wise technical specification (plain multi-line text) */
  specifications?: string | null;
  brand_offered?: string;
  is_alternate_item?: boolean;
  item_category?: string;
  lead_time_days?: number;
  notes?: string;
  /** Cumulative approved release qty on envelope parent lines */
  released_quantity?: number;
  /** Links release/call-off line to parent envelope line */
  parent_po_item_id?: string | null;
  /** Enriched from item master on read (not persisted on PO line). */
  profile_grade?: string | null;
  profile_heat_no?: string | null;
  profile_dimension?: string | null;
  profile_length?: string | null;
}

export interface PurchaseOrderOverheadDTO {
  id?: string;
  line_number?: number;
  overhead_type: string;
  description?: string | null;
  remarks?: string | null;
  currency?: string;
  exchange_rate?: number;
  original_amount: number;
  amount?: number;
  invoiced_amount?: number;
  base_currency_amount?: number;
}

export interface PurchaseOrderOverheadAllocationDTO {
  id: string;
  overhead_id: string;
  po_item_id: string;
  base_cost_amount: number;
  allocation_ratio: number;
  allocated_amount: number;
  allocation_basis: string;
}

export interface PurchaseOrderDTO {
  id: string;
  company_id: string;
  po_number: string;
  pr_id?: string;
  rfq_id?: string;
  bid_analysis_id?: string;
  supplier_inquiry_id?: string;
  supplier_quotation_id?: string;
  supplier_id: string;
  supplier_name: string;
  buyer?: string | null;
  next_action_user_id?: string | null;
  supplier_address?: string;
  supplier_contact?: string;
  po_date: string;
  po_type?: string;
  delivery_date?: string;
  delivery_to?: string;
  delivery_address?: string;
  site_location?: string;
  project_id?: string | null;
  department_id?: string | null;
  /** STANDARD | MARKET_PURCHASE (Spot) */
  procurement_method?: string | null;
  spot_purchaser_user_id?: string | null;
  spot_submission_id?: string | null;
  /** Envelope (Blanket/Planned/Contract) agreement window */
  agreement_start_date?: string | null;
  agreement_end_date?: string | null;
  max_value?: number | null;
  released_value?: number;
  /** Release/call-off parent link (concurrent children; not amendment) */
  parent_po_id?: string | null;
  project?: { id: string; project_name?: string; project_number?: string };
  items?: PurchaseOrderItemDTO[];
  subtotal: number;
  discount_percentage?: number;
  discount_amount?: number;
  total_after_discount?: number;
  vat_applicable?: boolean;
  vat_percentage?: number;
  vat_amount: number;
  freight_charges?: number;
  other_charges?: number;
  /** Sum of PO overhead rows (BY_COST_VALUE); included in grand_total. */
  total_overhead?: number;
  grand_total: number;
  currency: string;
  exchange_rate?: number;
  base_grand_total?: number | null;
  payment_terms?: string;
  payment_term_type?: string;
  milestone_total?: number;
  milestone_completed_value?: number;
  milestone_invoiced_value?: number;
  milestone_paid_value?: number;
  milestone_balance?: number;
  milestones?: PoMilestoneDTO[];
  overheads?: PurchaseOrderOverheadDTO[];
  overhead_allocations?: PurchaseOrderOverheadAllocationDTO[];
  delivery_terms?: string;
  quotation_reference?: string;
  validity_date?: string;
  advance_payment_percentage?: number;
  advance_payment_amount?: number;
  advance_required?: boolean;
  advance_type?: string;
  advance_reason?: string;
  expected_advance_date?: string;
  advance_payment_terms?: string;
  advance_approval_required?: boolean;
  advance_status?: string;
  advance_recovery_status?: string | null;
  supplier_reference?: string;
  po_status: string;
  approval_status: string;
  approval_instance_id?: string | null;
  approved_by?: string;
  approved_date?: string;
  revision_number?: number;
  amendment_of_po_id?: string;
  notes?: string;
  terms_and_conditions?: string;
  special_instructions?: string;
  created_at?: string;
  supplier?: {
    supplier_name?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    country?: string;
    postal_code?: string;
    tax_registration_number?: string;
    vat_number?: string;
  };
  bidAnalysis?: { id: string; analysis_number?: string };
  supplierInquiry?: { id: string; inquiry_number?: string };
  supplierQuotation?: { id: string; quotation_number?: string };
  bidAnalysisSources?: PoBidAnalysisSource[];
  purchaseRequisition?: { id: string; pr_number?: string };
  amendmentOf?: { id: string; po_number?: string; po_status?: string; revision_number?: number };
  amendments?: Array<{
    id: string;
    po_number?: string;
    po_status?: string;
    approval_status?: string;
    revision_number?: number;
  }>;
  envelopeParent?: { id: string; po_number?: string; po_status?: string; po_type?: string };
  envelopeChildren?: Array<{
    id: string;
    po_number?: string;
    po_status?: string;
    approval_status?: string;
    grand_total?: number;
    delivery_date?: string;
  }>;
}

export interface CreateEnvelopeChildLineInput {
  parent_po_item_id: string;
  ordered_quantity: number;
  project_id?: string | null;
  expected_delivery_date?: string | null;
}

export interface CreateEnvelopeChildPayload {
  delivery_date: string;
  lines: CreateEnvelopeChildLineInput[];
  notes?: string;
  project_id?: string | null;
  delivery_to?: string | null;
  delivery_address?: string | null;
}

export interface CreateFromBidAnalysisResult {
  purchaseOrders: PurchaseOrderDTO[];
}

export interface ConsolidationLineInput {
  bid_analysis_selection_id: string;
  award_qty: number;
}

export interface CreateFromBidAnalysesPayload {
  supplier_id: string;
  bid_analysis_ids: string[];
  lines: ConsolidationLineInput[];
  payment_terms?: string;
  delivery_terms?: string;
  quotation_reference?: string;
  mode?: 'new' | 'revise_existing';
  target_po_id?: string;
  department_id?: string;
}

export interface CreateFromBidAnalysisOptions {
  mode?: 'new' | 'revise_existing';
  target_po_id?: string;
  supplier_revisions?: Array<{ supplier_id: string; target_po_id: string }>;
  department_id?: string;
}

export interface EligiblePoForBaRevision {
  id: string;
  po_number: string;
  po_status: string;
  approval_status: string;
  revision_number: number;
  currency: string;
  supplier_id: string;
  supplier_name: string;
  grand_total: number;
  po_date?: string | null;
  amendment_of_po_id?: string | null;
  is_open_amendment_draft?: boolean;
}

export interface CreateFromBidAnalysesResult {
  purchaseOrder: PurchaseOrderDTO;
  warnings?: string[];
}

export interface PoBidAnalysisSource {
  bid_analysis_id: string;
  analysis_number?: string;
  supplier_inquiry_id?: string;
  inquiry_number?: string;
  supplier_quotation_ids: string[];
  quotation_numbers: string[];
}

export class PurchaseOrderService {
  async list(params?: {
    po_status?: string;
    approval_status?: string;
    supplier_id?: string;
    project_id?: string;
    date_from?: string;
    date_to?: string;
    bid_analysis_id?: string;
    supplier_inquiry_id?: string;
    search?: string;
    receivable?: boolean;
    /** When receivable: 'grn' | 'service_receipt' to split PO pickers */
    receipt_channel?: 'grn' | 'service_receipt';
    po_type?: string;
    parent_po_id?: string;
    envelope_only?: boolean;
    exclude_children?: boolean;
  }): Promise<PurchaseOrderDTO[]> {
    const q = new URLSearchParams();
    if (params?.po_status) q.set('po_status', params.po_status);
    if (params?.approval_status) q.set('approval_status', params.approval_status);
    if (params?.supplier_id) q.set('supplier_id', params.supplier_id);
    if (params?.project_id) q.set('project_id', params.project_id);
    if (params?.date_from) q.set('date_from', params.date_from);
    if (params?.date_to) q.set('date_to', params.date_to);
    if (params?.bid_analysis_id) q.set('bid_analysis_id', params.bid_analysis_id);
    if (params?.supplier_inquiry_id) q.set('supplier_inquiry_id', params.supplier_inquiry_id);
    if (params?.search) q.set('search', params.search);
    if (params?.receivable) q.set('receivable', '1');
    if (params?.receipt_channel) q.set('receipt_channel', params.receipt_channel);
    if (params?.po_type) q.set('po_type', params.po_type);
    if (params?.parent_po_id) q.set('parent_po_id', params.parent_po_id);
    if (params?.envelope_only) q.set('envelope_only', '1');
    if (params?.exclude_children) q.set('exclude_children', '1');
    const res = await api.get(`/procurement/purchase-orders?${q.toString()}`);
    const raw = unwrap<PurchaseOrderDTO[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async getById(id: string): Promise<PurchaseOrderDTO> {
    const res = await api.get(`/procurement/purchase-orders/${id}`);
    return unwrap<PurchaseOrderDTO>(res);
  }

  async getItems(poId: string): Promise<PurchaseOrderItemDTO[]> {
    const res = await api.get(`/procurement/purchase-orders/${poId}/items`);
    const raw = unwrap<PurchaseOrderItemDTO[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async create(payload: {
    po: Partial<PurchaseOrderDTO>;
    items: Partial<PurchaseOrderItemDTO>[];
    milestones?: PoMilestoneInput[];
    overheads?: Array<Partial<PurchaseOrderOverheadDTO>>;
  }): Promise<PurchaseOrderDTO> {
    const res = await api.post('/procurement/purchase-orders', payload);
    return unwrap<PurchaseOrderDTO>(res);
  }

  async createFromBidAnalysis(
    bid_analysis_id: string,
    options?: CreateFromBidAnalysisOptions
  ): Promise<CreateFromBidAnalysisResult> {
    const res = await api.post('/procurement/purchase-orders/from-bid-analysis', {
      bid_analysis_id,
      ...options,
    });
    return unwrap<CreateFromBidAnalysisResult>(res);
  }

  async createFromBidAnalyses(payload: CreateFromBidAnalysesPayload): Promise<CreateFromBidAnalysesResult> {
    const res = await api.post('/procurement/purchase-orders/from-bid-analyses', payload);
    return unwrap<CreateFromBidAnalysesResult>(res);
  }

  async listEligibleForBaRevision(
    supplierId: string,
    currency: string
  ): Promise<EligiblePoForBaRevision[]> {
    const res = await api.get('/procurement/purchase-orders/eligible-for-ba-revision', {
      params: { supplier_id: supplierId, currency },
    });
    const raw = unwrap<EligiblePoForBaRevision[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async createAmendment(poId: string): Promise<PurchaseOrderDTO> {
    const res = await api.post(`/procurement/purchase-orders/${poId}/create-amendment`, {});
    return unwrap<PurchaseOrderDTO>(res);
  }

  async createRelease(poId: string, payload: CreateEnvelopeChildPayload): Promise<PurchaseOrderDTO> {
    const res = await api.post(`/procurement/purchase-orders/${poId}/releases`, payload);
    return unwrap<PurchaseOrderDTO>(res);
  }

  async createCallOff(poId: string, payload: CreateEnvelopeChildPayload): Promise<PurchaseOrderDTO> {
    const res = await api.post(`/procurement/purchase-orders/${poId}/call-offs`, payload);
    return unwrap<PurchaseOrderDTO>(res);
  }

  async listReleases(poId: string): Promise<PurchaseOrderDTO[]> {
    const res = await api.get(`/procurement/purchase-orders/${poId}/releases`);
    const raw = unwrap<PurchaseOrderDTO[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async submit(poId: string): Promise<PurchaseOrderDTO> {
    const res = await api.post(`/procurement/purchase-orders/${poId}/submit`, {});
    return unwrap<PurchaseOrderDTO>(res);
  }

  async approve(poId: string, remarks?: string): Promise<PurchaseOrderDTO> {
    const res = await api.post(`/procurement/purchase-orders/${poId}/approve`, remarks ? { remarks } : {});
    return unwrap<PurchaseOrderDTO>(res);
  }

  async rejectApproval(poId: string, payload: RejectionPayload): Promise<PurchaseOrderDTO> {
    const res = await api.post(`/procurement/purchase-orders/${poId}/reject-approval`, payload);
    return unwrap<PurchaseOrderDTO>(res);
  }

  async revokeApproval(poId: string, reason: string): Promise<PurchaseOrderDTO> {
    const res = await api.post(`/procurement/purchase-orders/${poId}/revoke-approval`, { reason });
    return unwrap<PurchaseOrderDTO>(res);
  }

  async cancel(poId: string): Promise<PurchaseOrderDTO> {
    const res = await api.post(`/procurement/purchase-orders/${poId}/cancel`, {});
    return unwrap<PurchaseOrderDTO>(res);
  }

  async sendToSupplier(poId: string): Promise<PurchaseOrderDTO> {
    const res = await api.post(`/procurement/purchase-orders/${poId}/send-to-supplier`, {});
    return unwrap<PurchaseOrderDTO>(res);
  }

  async update(
    poId: string,
    payload: {
      po: Partial<PurchaseOrderDTO>;
      items: Partial<PurchaseOrderItemDTO>[];
      milestones?: PoMilestoneInput[];
      overheads?: Array<Partial<PurchaseOrderOverheadDTO>>;
    }
  ): Promise<PurchaseOrderDTO> {
    const res = await api.put(`/procurement/purchase-orders/${poId}`, payload);
    return unwrap<PurchaseOrderDTO>(res);
  }

  async getAdvanceSummary(poId: string) {
    const res = await api.get(`/procurement/purchase-orders/${poId}/advance-summary`);
    return unwrap<{
      advance_cap: number;
      requested: number;
      paid: number;
      adjusted: number;
      remaining: number;
      unpaid_request_balance: number;
      advance_status: string;
      remaining_request_capacity: number;
      can_create_request: boolean;
      block_reason: string | null;
      has_draft_request: boolean;
      requests: Array<{
        id: string;
        advance_request_number: string;
        status: string;
        requested_amount: number;
        paid_amount: number;
        adjusted_amount: number;
      }>;
    }>(res);
  }
}

export const purchaseOrderService = new PurchaseOrderService();
